// Narrow boundary to the optional, locally bundled LiveKit SDK. No CDN scripts or media secrets.
// This is an adapter contract, not a replacement SDK implementation.
interface RemoteTrack { mediaStreamTrack: MediaStreamTrack; source: string }
interface Participant { identity: string; permissions?: { canPublish?: boolean; canPublishSources?: number[] } }
interface Room {
  on(event: string, callback: (...args: any[]) => void): Room;
  connect(url: string, token: string): Promise<void>;
  disconnect(): Promise<void>;
  localParticipant: Participant & {
    publishTrack(track: MediaStreamTrack, options: Record<string, unknown>): Promise<unknown>;
    unpublishTrack(track: MediaStreamTrack, stopOnUnpublish?: boolean): Promise<unknown>;
  };
}
interface Sdk { Room: new (options: Record<string, unknown>) => Room }
export class SfuTransport {
  private room: Room | null = null;
  private closed = false;
  private published = new Set<MediaStreamTrack>();
  private loadSdk: () => Promise<Sdk>;
  constructor(loadSdk: () => Promise<Sdk> = async () => { const url = "/assets/livekit.js"; return await import(url) as Sdk; }) { this.loadSdk = loadSdk; }
  async connect(url: string, token: string, events: {
    track(id: string, source: string, track: MediaStreamTrack, removed: boolean): void;
    state(state: "connected" | "reconnecting"): void;
    error(message: string): void;
    permissions(): void;
    speakers(ids: string[]): void;
    admit(): Promise<void>;
  }) {
    let sdk: Sdk;
    try { sdk = await this.loadSdk(); }
    catch { throw new Error("LiveKit SDK is missing from this build. Install dependencies and rebuild with MAJLIS_REQUIRE_SFU=1."); }
    if (this.closed) return;
    // The existing UI attaches native MediaStreams rather than SDK track elements, so SDK
    // visibility-based adaptiveStream must stay disabled (otherwise it pauses visible videos).
    // VoiceEngine owns capture lifetime. SDK's default would stop its processed microphone when
    // the server removes publishing permission, leaving an ended track after moderator unmute.
    const room = this.room = new sdk.Room({ adaptiveStream: false, dynacast: true, disconnectOnPageLeave: true, stopLocalTrackOnUnpublish: false });
    room.on("trackSubscribed", (t: RemoteTrack, _pub: unknown, p: Participant) => events.track(p.identity, t.source, t.mediaStreamTrack, false));
    room.on("trackUnsubscribed", (t: RemoteTrack, _pub: unknown, p: Participant) => events.track(p.identity, t.source, t.mediaStreamTrack, true));
    room.on("reconnecting", () => events.state("reconnecting"));
    room.on("signalReconnecting", () => events.state("reconnecting"));
    room.on("reconnected", () => events.state("connected"));
    // participant_joined webhooks occur after media connects. Admit at signaling time as
    // well, so a token with no media grants cannot deadlock waiting for a media webhook.
    room.on("signalConnected", () => { void events.admit().catch((e) => { if (!this.closed) events.error(String((e as Error).message ?? e)); }); });
    room.on("participantPermissionsChanged", () => events.permissions());
    room.on("activeSpeakersChanged", (participants: Participant[]) => events.speakers(participants.map((p) => p.identity)));
    room.on("localTrackUnpublished", (publication: { track?: RemoteTrack }) => { if (publication.track) this.published.delete(publication.track.mediaStreamTrack); });
    room.on("disconnected", () => { if (!this.closed) events.error("Media disconnected. Leave voice and rejoin to retry."); });
    room.on("trackSubscriptionFailed", () => events.error("A media stream could not be received. Check the SFU connection."));
    await room.connect(url, token);
    if (this.closed) { await room.disconnect(); return; }
    events.state("connected"); events.permissions();
  }
  async publish(track: MediaStreamTrack, source: string, maxBitrate?: number, maxFramerate?: number, degradationPreference?: RTCDegradationPreference) {
    const room = this.room;
    if (this.closed || !room || this.published.has(track)) return;
    const permission = room.localParticipant.permissions;
    const sourceId = { camera: 1, microphone: 2, screen_share: 3, screen_share_audio: 4 }[source];
    if (sourceId === undefined || !permission?.canPublish || (permission.canPublishSources?.length && !permission.canPublishSources.includes(sourceId))) return;
    this.published.add(track);
    try {
      const encoding = maxBitrate ? { maxBitrate, ...(maxFramerate ? { maxFramerate } : {}) } : undefined;
      // LiveKit uses a separate screen-share encoding option. Passing videoEncoding
      // for a screen leaves the SDK's 15-fps default in effect regardless of capture.
      const videoOptions = source === "screen_share" ? { screenShareEncoding: encoding, degradationPreference }
        : source === "camera" ? { videoEncoding: encoding, degradationPreference } : {};
      await room.localParticipant.publishTrack(track, { source, simulcast: source === "camera", ...videoOptions,
        audioPreset: { maxBitrate: 64000 }, dtx: true, red: true, stopMicTrackOnMute: false });
      if (this.closed || track.readyState === "ended") await room.localParticipant.unpublishTrack(track, false);
    } catch (e) { this.published.delete(track); throw e; }
  }
  async unpublish(track: MediaStreamTrack) {
    this.published.delete(track);
    if (this.room) await this.room.localParticipant.unpublishTrack(track, false);
  }
  close() { this.closed = true; this.published.clear(); void this.room?.disconnect().catch(() => {}); this.room = null; }
}
